import { useEffect, useMemo, useState } from 'react';

import { FeatureToggleGroup } from 'shared/ui';
import { FEATURE_FLAG_KEYS, ProjectFeatureFlags } from 'shared/utils/feature-flags';

import { Button } from '@/components/ui/button';

import { useUpdateSubProjectMutation } from '../services/projects.api';
import { SubProjectDetail } from './project-detail.types';

interface Props {
  project: SubProjectDetail;
}

const buildFlags = (project: SubProjectDetail): ProjectFeatureFlags =>
  FEATURE_FLAG_KEYS.reduce((acc, key) => {
    acc[key] = project[key] ?? false;
    return acc;
  }, {} as ProjectFeatureFlags);

const extractErrorMessage = (error: unknown, fallback: string): string => {
  if (error instanceof Error) return error.message;
  if (typeof error === 'object' && error !== null && 'data' in error) {
    const { data } = error as { data: unknown };
    if (typeof data === 'string') return data;
  }
  return fallback;
};

const SubProjectCapabilitiesSection = ({ project }: Props) => {
  const [updateSubProject, { isLoading }] = useUpdateSubProjectMutation();
  const [flags, setFlags] = useState<ProjectFeatureFlags>(() => buildFlags(project));
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setFlags(buildFlags(project));
  }, [project]);

  const patch = useMemo(() => {
    const original = buildFlags(project);
    return FEATURE_FLAG_KEYS.reduce((acc, key) => {
      if (flags[key] !== original[key]) acc[key] = flags[key];
      return acc;
    }, {} as Partial<ProjectFeatureFlags>);
  }, [flags, project]);

  const isDirty = Object.keys(patch).length > 0;

  const handleSave = async () => {
    if (!isDirty) return;
    setError(null);

    try {
      await updateSubProject({
        id: project.id,
        masterProjectId: project.masterProjectId,
        ...patch,
      }).unwrap();
    } catch (saveError) {
      setError(extractErrorMessage(saveError, 'No se pudo guardar las funcionalidades.'));
    }
  };

  return (
    <section className="space-y-4">
      <FeatureToggleGroup value={flags} onChange={setFlags} />
      <div className="flex items-start justify-end gap-4">
        <Button
          type="button"
          onClick={() => {
            void handleSave();
          }}
          disabled={!isDirty || isLoading}
        >
          {isLoading ? 'Guardando...' : 'Guardar'}
        </Button>
      </div>
      {error && (
        <p className="rounded-md border border-destructive bg-destructive/10 p-3 text-sm text-destructive">{error}</p>
      )}
    </section>
  );
};

export default SubProjectCapabilitiesSection;
